/**
 * Cart Upsell Quick Buy Integration
 * Handles quick buy functionality for cart drawer upsells
 */

class CartUpsellQuickBuy {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.observeCartDrawer();
    
    // Check current state
    const quickbuyBlocks = document.querySelectorAll('.cart-upsell-quickbuy-block');
    const quickbuyToggles = document.querySelectorAll('.quickbuy-toggle');
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
    
    // Check theme quickbuy system
    if (typeof window.theme === 'undefined') {
      // Theme object not found
    }
  }

  bindEvents() {
    // Handle single variant add to cart
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('cart-upsell-single-variant-form')) {
        this.handleSingleVariantSubmit(e);
      }
    });

    // Handle quick buy button clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('cart-upsell-quickbuy-btn')) {
        this.handleQuickBuyClick(e);
      }
    });

    // Modal quick buy: inline .quickbuy-container is hidden in CSS; always use modal for .quickbuy-toggle
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.quickbuy-toggle');
      if (!toggle) return;
      const block = toggle.closest('.product-block');
      if (!block) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (document.querySelector('.quickbuy-modal-overlay')) {
        return;
      }
      this.openQuickBuyFallback(toggle, block);
    }, true);

    // Debug quick buy clicks in cart drawer
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quickbuy-toggle')) {
        const block = e.target.closest('.product-block');
        const quickbuyCont = block ? block.querySelector('.quickbuy-container') : null;
        const slider = e.target.closest('.collection-slider');
        const sliderRow = slider ? slider.closest('.collection-slider-row') : null;

        // If we're on desktop and in cart drawer, set up fallback detection
        if (window.innerWidth >= 768 && e.target.closest('.cart-drawer')) {
          // Prevent multiple modals
          if (document.querySelector('.quickbuy-modal-overlay')) {
            return;
          }
          
          // Let the theme try first, then check if it worked
          setTimeout(() => {
            // Double-check no modal was opened in the meantime
            if (document.querySelector('.quickbuy-modal-overlay')) {
              return;
            }
            
            const expandedBlock = block && block.classList.contains('expanded');
            
            if (!expandedBlock) {
              this.openQuickBuyFallback(e.target, block);
            }
          }, 150);
        }
      }
    });
  }

  observeCartDrawer() {
    // Re-initialize when cart drawer content changes
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.addedNodes.length > 0) {
            // Check if quickbuy blocks were added
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // Element node
                const quickbuyBlocks = node.querySelectorAll ? node.querySelectorAll('.cart-upsell-quickbuy-block') : [];
              }
            });
          }
        });
        this.reinitializeButtons();
      });
      
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  }

  reinitializeButtons() {
    // Rebind events after cart drawer content updates
    setTimeout(() => {
      this.bindEvents();
    }, 100);
  }

  async handleSingleVariantSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const button = form.querySelector('.cart-upsell-add-btn');
    const btnText = button.querySelector('.btn-text');
    const btnLoading = button.querySelector('.btn-loading');
    
    // Show loading state
    this.setButtonLoading(button, true);
    
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update cart drawer
        await this.refreshCartDrawer();
        
        // Show success feedback
        btnText.textContent = 'Added!';
        
        // Trigger cart update event
        this.dispatchCartUpdateEvent(data);
        
        // If we're in a modal, close it after showing success
        const modalOverlay = form.closest('.quickbuy-modal-overlay');
        if (modalOverlay) {
          setTimeout(() => {
            modalOverlay.classList.remove('open');
            setTimeout(() => {
              if (modalOverlay.parentNode) {
                modalOverlay.parentNode.removeChild(modalOverlay);
              }
            }, 300);
          }, 1000);
        } else {
          // For non-modal context, reset button as before
          setTimeout(() => {
            btnText.textContent = form.dataset.buttonText || 'Quick Add';
            this.setButtonLoading(button, false);
          }, 2000);
        }
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      this.setButtonLoading(button, false);
      btnText.textContent = 'Error';
      setTimeout(() => {
        btnText.textContent = form.dataset.buttonText || 'Quick Add';
      }, 2000);
    }
  }

  handleQuickBuyClick(e) {
    const button = e.target;
    const productUrl = button.dataset.productUrl;
    const productId = button.dataset.productId;
    
    // Try different methods to open quick buy modal
    if (this.openThemeQuickBuy(productUrl, productId)) {
      return;
    }
    
    if (this.openQuickBuyModal(productUrl, productId)) {
      return;
    }
    
    // Fallback: redirect to product page
    window.location.href = productUrl;
  }

  openThemeQuickBuy(productUrl, productId) {
    // Try theme's built-in quick buy functionality
    if (window.theme && window.theme.quickBuy) {
      window.theme.quickBuy.open(productUrl, productId);
      return true;
    }
    
    if (window.QuickBuy && typeof window.QuickBuy.open === 'function') {
      window.QuickBuy.open(productUrl, productId);
      return true;
    }
    
    return false;
  }

  openQuickBuyModal(productUrl, productId) {
    // Create a modal dynamically if no quick buy system exists
    const modal = this.createQuickBuyModal(productUrl, productId);
    if (modal) {
      document.body.appendChild(modal);
      modal.style.display = 'block';
      return true;
    }
    
    return false;
  }

  createQuickBuyModal(productUrl, productId) {
    // Create a basic modal structure
    const modal = document.createElement('div');
    modal.className = 'cart-upsell-modal';
    modal.innerHTML = `
      <div class="cart-upsell-modal__backdrop">
        <div class="cart-upsell-modal__content">
          <div class="cart-upsell-modal__header">
            <button type="button" class="cart-upsell-modal__close">&times;</button>
          </div>
          <div class="cart-upsell-modal__body">
            <iframe src="${productUrl}" style="width: 100%; height: 500px; border: none;"></iframe>
          </div>
        </div>
      </div>
    `;
    
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .cart-upsell-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
        display: none;
      }
      
      .cart-upsell-modal__backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .cart-upsell-modal__content {
        background: white;
        max-width: 90vw;
        max-height: 90vh;
        overflow: hidden;
        border-radius: 8px;
      }
      
      .cart-upsell-modal__header {
        padding: 1rem;
        border-bottom: 1px solid #eee;
        text-align: right;
      }
      
      .cart-upsell-modal__close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
      }
    `;
    
    modal.appendChild(style);
    
    // Close modal functionality
    modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('cart-upsell-modal__backdrop') || 
          e.target.classList.contains('cart-upsell-modal__close')) {
        modal.remove();
      }
    });
    
    return modal;
  }

  setButtonLoading(button, loading) {
    const btnText = button.querySelector('.btn-text');
    const btnLoading = button.querySelector('.btn-loading');
    
    if (loading) {
      button.classList.add('loading');
      button.disabled = true;
      if (btnLoading) btnLoading.style.display = 'inline-block';
      if (btnText) btnText.style.display = 'none';
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      if (btnLoading) btnLoading.style.display = 'none';
      if (btnText) btnText.style.display = 'inline';
    }
  }

  async refreshCartDrawer() {
    // Try to refresh cart drawer if it's available
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      await cartDrawer.refresh();
    } else if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.refresh === 'function') {
      await window.theme.cartDrawer.refresh();
    } else {
      // Trigger a cart update event
      this.dispatchCartUpdateEvent();
    }
  }

  dispatchCartUpdateEvent(data = null) {
    // Dispatch custom event for cart update
    const event = new CustomEvent('cart:updated', {
      detail: data,
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  // Legacy method removed - using modal approach instead

  processQuickBuyContent(content, productUrl) {
    // Process content like the theme does
    content.querySelectorAll('.more').forEach(el => { el.href = productUrl; });
    content.querySelectorAll('.product-title').forEach(el => {
      el.innerHTML = `<a href="${productUrl}">${el.innerHTML}</a>`;
    });
    content.querySelectorAll('.show-gallery').forEach(el => {
      el.classList.remove('show-gallery');
      el.href = productUrl;
    });
    content.querySelectorAll('.not-in-quickbuy').forEach(el => el.remove());
    content.querySelectorAll('.only-in-quickbuy').forEach(el => el.classList.remove('only-in-quickbuy'));
    content.querySelectorAll('.sticky-content-container').forEach(el => el.classList.remove('sticky-content-container'));
    content.querySelectorAll('pickup-availability').forEach(el => el.remove());
    content.querySelectorAll('variant-picker[data-update-url="true"]').forEach(el => el.dataset.updateUrl = false);
    content.querySelectorAll('noscript').forEach(el => el.remove());
  }

  cleanupQuickBuyContent(container) {
    // Remove "Pairs well with" section - try multiple selectors
    const pairsWellWithSelectors = [
      '[class*="pairs-well"]',
      '[class*="recommended"]',
      '[class*="related"]',
      '[class*="upsell"]',
      '[data-section*="related"]',
      '[data-section*="recommendation"]'
    ];
    
    pairsWellWithSelectors.forEach(selector => {
      const elements = container.querySelectorAll(selector);
      elements.forEach(el => {
        el.remove();
      });
    });
    
    // Remove by text content (case insensitive)
    const textPatterns = [
      /pairs\s+well\s+with/i,
      /recommended\s+products/i,
      /you\s+might\s+also\s+like/i,
      /related\s+products/i,
      /frequently\s+bought\s+together/i
    ];
    
    const allElements = container.querySelectorAll('*');
    allElements.forEach(el => {
      if (el.textContent) {
        textPatterns.forEach(pattern => {
          if (pattern.test(el.textContent.trim())) {
            el.remove();
          }
        });
      }
    });
    
    // Remove empty containers that might be left behind
    const emptyContainers = container.querySelectorAll('div:empty, section:empty, aside:empty');
    emptyContainers.forEach(el => {
      el.remove();
    });
  }

  closeQuickBuyFallback(block) {
    // Clean up observers and intervals
    const quickbuyCont = block.querySelector('.quickbuy-container');
    if (quickbuyCont) {
      // Clean up mutation observer
      if (quickbuyCont._variantObserver) {
        quickbuyCont._variantObserver.disconnect();
        quickbuyCont._variantObserver = null;
      }
      
      // Clean up polling interval
      if (quickbuyCont._variantPollInterval) {
        clearInterval(quickbuyCont._variantPollInterval);
        quickbuyCont._variantPollInterval = null;
      }
      
      block.classList.remove('expanded');
      quickbuyCont.style.height = '0px';
      setTimeout(() => {
        if (quickbuyCont.style.height === '0px') {
          quickbuyCont.querySelector('.inner').innerHTML = '';
        }
      }, 700); // Match theme transition duration
    }
  }

  openQuickBuyFallback(toggleElement, productBlock) {
    // Prevent multiple modals from opening
    if (document.querySelector('.quickbuy-modal-overlay')) {
      return;
    }
    
    if (!productBlock) {
      return;
    }
    
    const productUrl = toggleElement.href;
    
    // Create modal overlay and append to body
    const modalOverlay = document.createElement('div');
    modalOverlay.className = 'quickbuy-modal-overlay';
    modalOverlay.innerHTML = `
      <div class="quickbuy-modal">
        <div class="quickbuy-modal-header">
          <button class="quickbuy-modal-close" aria-label="Close">×</button>
        </div>
        <div class="quickbuy-modal-content">
          <div class="quickbuy-loading">Loading...</div>
        </div>
      </div>
    `;
    
    // Add styles for the modal
    const modalStyles = document.createElement('style');
    modalStyles.textContent = `
      .quickbuy-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      
      .quickbuy-modal-overlay.open {
        opacity: 1;
      }
      
      .quickbuy-modal {
        background: white;
        border-radius: 8px;
        max-width: 600px;
        max-height: 80vh;
        width: 90%;
        overflow: hidden;
        transform: scale(0.9);
        transition: transform 0.3s ease;
      }
      
      .quickbuy-modal-overlay.open .quickbuy-modal {
        transform: scale(1);
      }
      
      .quickbuy-modal-header {
        padding: 16px 20px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .quickbuy-modal-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        margin-left: auto;
      }
      
      .quickbuy-modal-content {
        padding: 20px;
        max-height: 60vh;
        overflow-y: auto;
      }
      
      .quickbuy-loading {
        text-align: center;
        padding: 40px 20px;
      }
      
      .quickbuy-form {
        margin-top: 20px;
      }
      
      .quickbuy-gallery {
        margin-bottom: 20px;
        max-height: 300px;
        overflow: hidden;
      }
      
      .quickbuy-gallery img {
        max-width: 100%;
        height: auto;
        object-fit: cover;
      }
      
      .quickbuy-title {
        margin-bottom: 15px;
        font-size: 1.2em;
        font-weight: bold;
      }
      
      /* Out of stock button styles */
      .quickbuy-modal button.btn--unavailable,
      .quickbuy-modal button.disabled,
      .quickbuy-modal button[disabled] {
        opacity: 0.6 !important;
        cursor: not-allowed !important;
        pointer-events: none;
        background-color: #ccc !important;
        border-color: #ccc !important;
        color: #666 !important;
      }
      
      .quickbuy-modal .btn--unavailable:hover,
      .quickbuy-modal .disabled:hover,
      .quickbuy-modal button[disabled]:hover {
        transform: none !important;
        box-shadow: none !important;
      }
    `;
    
    // Append modal and styles to body
    document.head.appendChild(modalStyles);
    document.body.appendChild(modalOverlay);
    
    // Set up close functionality
    const closeModal = () => {
      modalOverlay.classList.remove('open');
      setTimeout(() => {
        if (modalOverlay.parentNode) {
          modalOverlay.parentNode.removeChild(modalOverlay);
        }
        if (modalStyles.parentNode) {
          modalStyles.parentNode.removeChild(modalStyles);
        }
      }, 300);
    };
    
    modalOverlay.querySelector('.quickbuy-modal-close').addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        closeModal();
      }
    });
    
    // Show modal with animation
    setTimeout(() => {
      modalOverlay.classList.add('open');
    }, 10);
    
    // Load the product page content
    fetch(productUrl)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then(html => {
        
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Look for the quickbuy-content element first (theme's expected format)
        const quickbuyContent = tempDiv.querySelector('.quickbuy-content');
        
        // Debug: Check for JSON data in the fetched content
        const jsonElements = tempDiv.querySelectorAll('script[type="application/json"]');
        
        const modalContent = modalOverlay.querySelector('.quickbuy-modal-content');
        
        if (quickbuyContent) {
          // Clone the quickbuy-content element exactly as theme expects
          const newDetail = quickbuyContent.cloneNode(true);
          
          // Apply the same transformations the theme does
          newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
          newDetail.querySelectorAll('.product-title').forEach((el) => {
            el.innerHTML = `<a href="">${el.innerHTML}</a>`;
            el.firstElementChild.href = productUrl;
          });
          newDetail.querySelectorAll('.show-gallery').forEach((el) => {
            el.classList.remove('show-gallery');
            el.href = productUrl;
          });
          newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
          newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
          newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
          newDetail.querySelectorAll('noscript').forEach((el) => el.remove());

          // Apply gallery layout transformations (exactly like theme)
          newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
          newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
          ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
            newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
              el.classList.remove(cl);
              el.classList.add('media-gallery--layout-carousel-under');
            });
          });
          
          // Remove unwanted sections from the form
          this.cleanupQuickBuyContent(newDetail);
          
          // Add any external resources (exactly like theme)
          tempDiv.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
            if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
              document.head.insertAdjacentHTML('beforeend', el.outerHTML);
            }
          });
          tempDiv.querySelectorAll('.section-main-product script[src]').forEach((el) => {
            if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
              const scr = document.createElement('script');
              scr.src = el.getAttribute('src');
              scr.defer = 'defer';
              document.head.appendChild(scr);
            }
          });
          
          // Clear and populate the modal content
          modalContent.innerHTML = '';
          modalContent.appendChild(newDetail);
          
          // Initialize any scripts needed for the form
          this.initializeQuickBuyForm(modalContent);
          
          // Set up form submission to close modal on success
          const form = modalContent.querySelector('form[action*="/cart/add"]');
          if (form) {
            form.addEventListener('submit', async (e) => {
              e.preventDefault();
              
              // Get the submit button to show loading state
              const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
              const originalBtnText = submitBtn ? submitBtn.textContent : '';
              
              if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Adding...';
              }
              
              try {
                const formData = new FormData(form);
                const response = await fetch(form.action, {
                  method: 'POST',
                  body: formData
                });
                
                if (response.ok) {
                  // Success! Update the button text briefly
                  if (submitBtn) {
                    submitBtn.textContent = 'Added!';
                  }
                  
                  // Refresh cart drawer if it exists
                  await this.refreshCartDrawer();
                  
                  // Dispatch cart update event
                  const data = await response.json().catch(() => null);
                  this.dispatchCartUpdateEvent(data);
                  
                  // Close modal after brief success indication
                  setTimeout(() => {
                    closeModal();
                  }, 800);
                } else {
                  throw new Error(`HTTP ${response.status}`);
                }
              } catch (error) {
                // Reset button and show error
                if (submitBtn) {
                  submitBtn.disabled = false;
                  submitBtn.textContent = 'Error - Try Again';
                  
                  // Reset to original text after 3 seconds
                  setTimeout(() => {
                    submitBtn.textContent = originalBtnText;
                  }, 3000);
                }
              }
            });
          }
        } else {
          // Fallback: Try the old method
          const productForm = tempDiv.querySelector('.product-form') || 
                             tempDiv.querySelector('form[action*="/cart/add"]') ||
                             tempDiv.querySelector('.js-product');
          
          if (productForm) {
            const formClone = productForm.cloneNode(true);
            this.cleanupQuickBuyContent(formClone);
            modalContent.innerHTML = '';
            modalContent.appendChild(formClone);
            this.initializeQuickBuyForm(modalContent);
          } else {
            // Ultimate fallback: create a simple "View Product" link
            modalContent.innerHTML = `
              <div style="padding: 20px; text-align: center;">
                <p>Quick buy not available for this product.</p>
                <a href="${productUrl}" class="btn btn--primary" onclick="window.open('${productUrl}', '_blank')">View Product</a>
              </div>
            `;
          }
        }
      })
      .catch(error => {
        const modalContent = modalOverlay.querySelector('.quickbuy-modal-content');
        modalContent.innerHTML = `
          <div style="padding: 20px; text-align: center;">
            <p>Failed to load quick buy. Please try again.</p>
            <a href="${productUrl}" class="btn btn--primary" onclick="window.open('${productUrl}', '_blank')">View Product</a>
          </div>
        `;
      });
  }
  
  initializeQuickBuyForm(container) {
    // Make IDs unique within popup to prevent conflicts with main page
    this.makePopupIdsUnique(container);
    
    // Initialize variant tracking
    let lastVariantValue = null;
    
    // The container should now contain the .quickbuy-content element (which is also .js-product)
    const productContainer = container.querySelector('.js-product') || container;
    
    if (productContainer && productContainer.classList.contains('js-product')) {
      // Ensure the container has the proper section data
      if (!productContainer.dataset.section) {
        // Generate a unique section ID for this modal instance
        const sectionId = 'quickbuy-modal-' + Date.now();
        productContainer.dataset.section = sectionId;
      }
      
      // Find the variant picker element
      const variantPicker = productContainer.querySelector('variant-picker');
      
      if (variantPicker) {
        // Ensure proper setup for quickbuy
        variantPicker.dataset.updateUrl = 'false'; // Prevent URL updates in modal
        
        // Remove loaded attribute to force re-initialization
        if (variantPicker.hasAttribute('loaded')) {
          variantPicker.removeAttribute('loaded');
        }
        
        // Re-trigger initialization by calling the constructor manually
        const VariantPickerClass = customElements.get('variant-picker');
        if (VariantPickerClass) {
          try {
            // Clear any existing state first
            variantPicker.section = null;
            variantPicker.productForm = null;
            variantPicker.optionSelectors = null;
            variantPicker.nativeSelector = null;
            variantPicker.data = null;
            variantPicker.selectedOptions = null;
            variantPicker.variant = null;
            variantPicker.forms = null;
            
            // Ensure proper section setup before calling constructor
            const section = variantPicker.closest('.js-product');
            if (section) {
            } else {
              productContainer.classList.add('js-product');
            }
            
            // Try to manually set up the basic properties instead of calling constructor
            variantPicker.section = variantPicker.closest('.js-product') || productContainer;
            variantPicker.optionSelectors = variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');
            variantPicker.nativeSelector = variantPicker.querySelector('[name="id"]');
            variantPicker.setAttribute('loaded', '');
            
            
            // Verify data is loaded after initialization
            setTimeout(() => {
              if (!variantPicker.data) {
                try {
                  variantPicker.data = variantPicker.getVariantData();
                } catch (error) {
                }
              }
              
              if (!variantPicker.optionSelectors || variantPicker.optionSelectors.length === 0) {
                const selectors = variantPicker.querySelectorAll('.option-selector, select[name*="option"], input[type="radio"][name*="option"]');
                if (selectors.length > 0) {
                  variantPicker.optionSelectors = selectors;
                }
              }
            }, 100);
            
            // Debug the re-initialized state
            
            // If optionSelectors is still null, try to find them manually
            if (!variantPicker.optionSelectors || variantPicker.optionSelectors.length === 0) {
              const manualSelectors = variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');
              if (manualSelectors.length > 0) {
                variantPicker.optionSelectors = manualSelectors;
              } else {
                // Try broader selectors
                const allSelectors = variantPicker.querySelectorAll('select, input[type="radio"]');
                variantPicker.optionSelectors = allSelectors;
              }
            }
            
          } catch (error) {
          }
        }
        
        // Force proper event binding
        setTimeout(() => {
          if (!variantPicker.hasAttribute('loaded')) {
            
            // Manually trigger connected callback if available
            if (variantPicker.connectedCallback) {
              variantPicker.connectedCallback();
            }
            
            // If still not loaded, create a new instance
            if (!variantPicker.hasAttribute('loaded') && VariantPickerClass) {
              // Clear existing properties
              variantPicker.section = null;
              variantPicker.productForm = null;
              variantPicker.optionSelectors = null;
              variantPicker.nativeSelector = null;
              variantPicker.data = null;
              variantPicker.selectedOptions = null;
              variantPicker.variant = null;
              variantPicker.forms = null;
              
              // Ensure the container has proper structure
              const section = variantPicker.closest('.js-product');
              if (!section) {
                productContainer.classList.add('js-product');
                
                // Also ensure section has proper data attribute
                if (!productContainer.dataset.section) {
                  productContainer.dataset.section = 'quickbuy-modal-' + Date.now();
                }
              }
              
              // Re-initialize manually with proper context
              try {
                variantPicker.section = variantPicker.closest('.js-product') || productContainer;
                variantPicker.optionSelectors = variantPicker.querySelectorAll('.option-selector:not(.option-selector--custom)');
                variantPicker.nativeSelector = variantPicker.querySelector('[name="id"]');
                variantPicker.setAttribute('loaded', '');
              } catch (error) {
              }
              
              // Double-check optionSelectors after construction
              if (!variantPicker.optionSelectors || variantPicker.optionSelectors.length === 0) {
                const foundSelectors = variantPicker.querySelectorAll('.option-selector, select[name*="option"], input[type="radio"][name*="option"]');
                if (foundSelectors.length > 0) {
                  variantPicker.optionSelectors = foundSelectors;
                }
              }
            }
          }
          
          // Final check and debug logging
          setTimeout(() => {
          
          // Test all methods to see which ones fail
          try {
            const options = variantPicker.getSelectedOptions();
          } catch (error) {
          }
          
          try {
            const data = variantPicker.getVariantData();
          } catch (error) {
          }
          
          try {
            const variant = variantPicker.getSelectedVariant();
          } catch (error) {
          }
          
          try {
            variantPicker.updateAvailability();
          } catch (error) {
          }
          
          try {
            variantPicker.updateAddToCartButton();
          } catch (error) {
          }
          
          try {
            variantPicker.handleVariantChange({ type: 'test' });
          } catch (error) {
          }
          
          // Call our custom update method as well
          try {
            this.updateAddToCartButton(productContainer);
          } catch (error) {
          }
          }, 200);
        }, 100);
      } else {
        
        // Look for any form with variant selection
        const forms = productContainer.querySelectorAll('form[action*="/cart/add"]');
        if (forms.length > 0) {
          forms.forEach((form, index) => {
            const variantInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
            
            if (variantInputs.length > 0) {
              // Set up manual variant tracking (scoped to popup)
              variantInputs.forEach(input => {
                input.addEventListener('change', (e) => {
                  
                  // Stop propagation to prevent affecting main page
                  e.stopPropagation();
                  
                  lastVariantValue = e.target.value;
                  
                  // Update all other variant inputs in THIS form only
                  const allInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
                  allInputs.forEach(otherInput => {
                    if (otherInput !== e.target) {
                      otherInput.value = e.target.value;
                    }
                  });
                  
                  // Update add to cart button availability
                  setTimeout(() => {
                    this.updateAddToCartButton(container);
                    this.updateFeaturedImage(container);
                  }, 50);
                });
              });
            }
          });
        }
      }
      
      // Add safety patch for variant picker methods
      const existingVariantPicker = productContainer.querySelector('variant-picker');
      if (existingVariantPicker) {
        // Apply patches immediately and also after delays
        this.applyVariantPickerPatches(productContainer);
      }
      
      // Add comprehensive debugging for variant selection
      setTimeout(() => {        
        // Debug variant selection (uncomment for troubleshooting)
        // this.debugVariantSelection(productContainer);
      }, 500);
      
      // Set up our custom event handlers with popup isolation
      const variantChangeHandler = (event) => {
        
        // Stop propagation to prevent affecting main page variant selection
        event.stopPropagation();
        
        if (event.detail && event.detail.variant) {
          const variantId = event.detail.variant.id;
          
          // Update ONLY hidden inputs within THIS specific popup container
          const hiddenInputs = container.querySelectorAll('input[type="hidden"][name="id"], input[name="id"]');
          hiddenInputs.forEach(input => {
            input.value = variantId;
          });
          
          // Update the polling tracker for this popup only
          lastVariantValue = variantId;
          
          // Update add to cart button availability
          this.updateAddToCartButton(container);
          
          // Update featured image
          this.updateFeaturedImage(container);
        }
      };
      
      // Listen for theme's custom variant change events
      productContainer.addEventListener('on:variant:change', variantChangeHandler);
      
    } else {
    }
    
    // Initialize variant selectors if they exist (fallback)
    const allVariantSelectors = container.querySelectorAll('select[name="id"], input[name="id"], input[type="radio"][name="id"]');
    allVariantSelectors.forEach((selector, index) => {
    });
    
    // Backup event handlers for variant changes (scoped to popup only)
    container.addEventListener('change', (event) => {
      
      // Stop propagation to prevent affecting main page
      event.stopPropagation();
      
      // If this is a variant selection change, update variant inputs ONLY within this popup
      if (event.target.name === 'id' || event.target.getAttribute('name') === 'id') {
        const selectedValue = event.target.value;
        
        // Update ONLY inputs with name="id" in THIS popup container
        const allVariantInputs = container.querySelectorAll('input[name="id"], select[name="id"]');
        
        allVariantInputs.forEach((input, index) => {
          if (input !== event.target) {
            input.value = selectedValue;
          }
        });
        
        // Update the polling tracker for this popup
        lastVariantValue = selectedValue;
        
        // Update add to cart button availability
        this.updateAddToCartButton(container);
        
        // Update featured image
        this.updateFeaturedImage(container);
      }
    });
    
    container.addEventListener('input', (event) => {
      
      // Stop propagation to prevent affecting main page
      event.stopPropagation();
      
      // If this is a variant selection input, update variant inputs ONLY within this popup
      if (event.target.name === 'id' || event.target.getAttribute('name') === 'id') {
        const selectedValue = event.target.value;
        
        // Update ONLY inputs with name="id" in THIS popup container
        const allVariantInputs = container.querySelectorAll('input[name="id"], select[name="id"]');
        
        allVariantInputs.forEach((input, index) => {
          if (input !== event.target) {
            input.value = selectedValue;
          }
        });
        
        // Update the polling tracker for this popup
        lastVariantValue = selectedValue;
        
        // Update add to cart button availability
        this.updateAddToCartButton(container);
        
        // Update featured image
        this.updateFeaturedImage(container);
      }
    });
    
    // Test if event listeners work at all
    setTimeout(() => {
      const testEvent = new CustomEvent('test-event', { detail: { test: true } });
      container.addEventListener('test-event', () => {
      });
      container.dispatchEvent(testEvent);
    }, 1000);
    
    // Simple polling approach as ultimate fallback
    const pollVariantChanges = () => {
      const currentVariantInput = container.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"]');
      if (currentVariantInput && currentVariantInput.value !== lastVariantValue) {
        lastVariantValue = currentVariantInput.value;
        
        // Update all form inputs
        const allFormInputs = container.querySelectorAll('input[name="id"]');
        allFormInputs.forEach(input => {
          if (input.value !== lastVariantValue) {
            input.value = lastVariantValue;
          }
        });
      }
    };
    
    // Poll every 500ms
    const pollInterval = setInterval(pollVariantChanges, 500);
    
    // Store interval reference for cleanup
    container._variantPollInterval = pollInterval;
     // Initial poll
    pollVariantChanges();
    
    // Set initial add to cart button state and featured image
    setTimeout(() => {
      this.updateAddToCartButton(container);
      this.updateFeaturedImage(container);
      
      // Also re-check after variant picker has had time to fully initialize
      setTimeout(() => {
        this.updateAddToCartButton(container);
      }, 500);
    }, 100);

    // Handle form submission
    const forms = container.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        // Check if the button is disabled (out of stock)
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"], .btn[type="submit"]');
        if (submitBtn && (submitBtn.disabled || submitBtn.classList.contains('btn--unavailable'))) {
          return;
        }

        // Get the currently selected variant - check multiple sources
        let selectedVariantId = null;
        
        // Method 1: Use the last tracked variant value first (most reliable)
        if (lastVariantValue) {
          selectedVariantId = lastVariantValue;
        }
        
        // Method 2: Check for checked radio buttons
        if (!selectedVariantId) {
          const checkedVariant = form.querySelector('input[name="id"]:checked');
          if (checkedVariant) {
            selectedVariantId = checkedVariant.value;
          }
        }
        
        // Method 3: Check selected options in dropdowns
        if (!selectedVariantId) {
          const selectedOption = form.querySelector('select[name="id"]');
          if (selectedOption && selectedOption.value) {
            selectedVariantId = selectedOption.value;
          }
        }
        
        // Method 4: Check any input with name="id" as final fallback
        if (!selectedVariantId) {
          const anyVariantInput = form.querySelector('input[name="id"]');
          if (anyVariantInput) {
            selectedVariantId = anyVariantInput.value;
          }
        }
        
        if (!selectedVariantId) {
          return;
        }
        
        
        // Ensure the form has the correct variant ID in ALL relevant inputs
        const allVariantInputs = form.querySelectorAll('input[name="id"]');
        allVariantInputs.forEach((input, index) => {
          input.value = selectedVariantId;
        });

        try {
          const formData = new FormData(form);
          for (let [key, value] of formData.entries()) {
          }
          
          const response = await fetch(form.action, {
            method: 'POST',
            body: formData
          });

          if (response.ok) {

            // Close the quick buy
            const productBlock = container.closest('.product-block');
            if (productBlock) {
              productBlock.classList.remove('expanded');
            }
            
            // If we're in a modal, close it
            const modalOverlay = container.closest('.quickbuy-modal-overlay');
            if (modalOverlay) {
              // Give a brief moment to show success, then close
              setTimeout(() => {
                modalOverlay.classList.remove('open');
                setTimeout(() => {
                  if (modalOverlay.parentNode) {
                    modalOverlay.parentNode.removeChild(modalOverlay);
                  }
                }, 300);
              }, 800);
            }
            
            // Refresh cart
            await this.refreshCartDrawer();
            
            // Show success message
            this.showSuccessMessage('Added to cart!');
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        } catch (error) {
          this.showErrorMessage('Failed to add to cart. Please try again.');
        }
      });
    });
  }
  
  // Update add to cart button based on variant availability
  updateAddToCartButton(container) {
    
    const forms = container.querySelectorAll('form[action*="/cart/add"]');
    const variantPicker = container.querySelector('variant-picker');
    
    forms.forEach(form => {
      const addBtn = form.querySelector('button[type="submit"], input[type="submit"], .btn[type="submit"]');
      if (!addBtn) return;
      
      // Get button text elements
      const btnTextEl = addBtn.querySelector('.btn-text') || addBtn;
      const originalText = addBtn.dataset.addToCartText || btnTextEl.textContent.trim() || 'Add to Cart';
      
      // Store original text if not already stored
      if (!addBtn.dataset.addToCartText && originalText && !originalText.includes('Sold out') && !originalText.includes('Unavailable')) {
        addBtn.dataset.addToCartText = originalText;
      }
      
      let variantAvailable = false;
      let currentVariant = null;
      let allOptionsSelected = false;
      
      // Check if variant picker exists and is properly initialized
      if (variantPicker) {
        // First check if all options are selected (following theme's pattern)
        if (variantPicker.selectedOptions && variantPicker.selectedOptions.indexOf(null) === -1) {
          allOptionsSelected = true;
          
          // Now check if we have a valid variant
          if (variantPicker.variant) {
            currentVariant = variantPicker.variant;
            variantAvailable = currentVariant.available;
          } else {
          }
        } else {
          // If not all options are selected, check if we can get variant data anyway
          const variantId = form.querySelector('input[name="id"]')?.value;
          if (variantId && variantPicker.data && variantPicker.data.variants) {
            currentVariant = variantPicker.data.variants.find(v => v.id == variantId);
            if (currentVariant) {
              variantAvailable = currentVariant.available;
              allOptionsSelected = true; // Treat as if selected if we have a variant ID
            }
          }
        }
      } else {
        // No variant picker - try to get variant ID directly from form
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (variantId) {
          // For products with only one variant, this is normal
          allOptionsSelected = true;
          variantAvailable = true; // Assume available unless we can check otherwise
        }
      }
      
      // Update button state based on findings
      if (!allOptionsSelected) {
        // Not all options selected - keep in default/unavailable state but don't show error
        addBtn.disabled = true;
        addBtn.classList.add('btn--unavailable', 'disabled');
        addBtn.style.opacity = '0.6';
        addBtn.style.cursor = 'not-allowed';
        btnTextEl.textContent = window.theme?.strings?.noVariant || 'Select options';
      } else if (variantAvailable) {
        // Available - enable button
        addBtn.disabled = false;
        addBtn.classList.remove('btn--unavailable', 'disabled');
        addBtn.style.opacity = '';
        addBtn.style.cursor = '';
        btnTextEl.textContent = addBtn.dataset.addToCartText || originalText;
      } else {
        // Not available - disable button
        addBtn.disabled = true;
        addBtn.classList.add('btn--unavailable', 'disabled');
        addBtn.style.opacity = '0.6';
        addBtn.style.cursor = 'not-allowed';
        
        // Use theme's out of stock text
        const unavailableText = currentVariant ? 
          (window.theme?.strings?.noStock || 'Sold out') : 
          (window.theme?.strings?.noVariant || 'Unavailable');
        
        btnTextEl.textContent = unavailableText;
      }
    });
  }

  showSuccessMessage(message) {
    // You could implement a toast notification here
  }
  
  showErrorMessage(message) {
    // You could implement an error notification here
  }
  
  debugVariantSelection(container) {
    
    // Find all potential variant-related elements
    const variantPicker = container.querySelector('variant-picker');
    const variantSelects = container.querySelectorAll('select[name*="id"], select[name*="option"]');
    const variantRadios = container.querySelectorAll('input[type="radio"][name*="option"], input[type="radio"][name*="id"]');
    const hiddenInputs = container.querySelectorAll('input[type="hidden"][name="id"]');
    const optionSelectors = container.querySelectorAll('.option-selector');
    const customSelects = container.querySelectorAll('.custom-select');
    const jsonDataElements = container.querySelectorAll('script[type="application/json"]');
    
    
    // Debug JSON data elements
    jsonDataElements.forEach((jsonEl, index) => {
      try {
        const data = JSON.parse(jsonEl.textContent);
        if (data.variants && data.variants.length > 0) {
        }
      } catch (error) {
      }
    });
    
    // Debug variant picker state
    if (variantPicker) {
      
      // Test getSelectedOptions
      try {
        const selectedOptions = variantPicker.getSelectedOptions();
      } catch (error) {
      }
    }
    
    // Debug each variant selection method
    
    variantSelects.forEach((select, index) => {
      if (select.options) {
        Array.from(select.options).forEach((option, optIndex) => {
        });
      }
    });
    
    variantRadios.forEach((radio, index) => {
    });
    
    hiddenInputs.forEach((input, index) => {
    });
    
    optionSelectors.forEach((selector, index) => {
      
      const checkedInput = selector.querySelector('input:checked');
      const selectedOption = selector.querySelector('.custom-select__option[aria-selected="true"]');
      
      
      if (checkedInput) {
      }
      if (selectedOption) {
      }
    });
    
    // Try to manually determine the selected variant
    
    let manualSelectedOptions = [];
    
    // Method 1: Use option selectors
    optionSelectors.forEach((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
        const value = selected && selected.dataset.value !== '' ? selected.dataset.value : null;
        manualSelectedOptions.push(value);
      } else {
        const selected = selector.querySelector('input:checked');
        const value = selected ? selected.value : null;
        manualSelectedOptions.push(value);
      }
    });
    
    
    // Method 2: Check if there's variant data to compare against
    if (variantPicker && variantPicker.data && variantPicker.data.variants) {
      variantPicker.data.variants.forEach((variant, index) => {
        
        // Check if this matches our manual selection
        const matches = variant.options.every((option, idx) => option === manualSelectedOptions[idx]);
        if (matches) {
        }
      });
    }
    
  }
  
  applyVariantPickerPatches(container) {
    
    const variantPicker = container.querySelector('variant-picker');
    if (!variantPicker) {
      return;
    }
    
    // Store original methods if not already stored
    if (!variantPicker._originalMethods) {
      variantPicker._originalMethods = {
        getSelectedOptions: variantPicker.getSelectedOptions,
        getVariantData: variantPicker.getVariantData,
        getSelectedVariant: variantPicker.getSelectedVariant,
        updateVariantInput: variantPicker.updateVariantInput,
        updateAvailability: variantPicker.updateAvailability,
        handleVariantChange: variantPicker.handleVariantChange
      };
    }
    
    const original = variantPicker._originalMethods;
    
    // Override getVariantData with a safer version
    variantPicker.getVariantData = function() {
      try {
        const dataEl = this.querySelector('[type="application/json"]');
        if (!dataEl) {
          return { variants: [] };
        }
        const data = JSON.parse(dataEl.textContent);
        return data;
      } catch (error) {
        return { variants: [] };
      }
    };
    
    // Override getSelectedVariant with null safety
    variantPicker.getSelectedVariant = function() {
      try {
        if (!this.data || !this.data.variants || !this.selectedOptions) {
          return null;
        }
        return original.getSelectedVariant.call(this);
      } catch (error) {
        return null;
      }
    };
    
    // Override getSelectedOptions with null safety
    variantPicker.getSelectedOptions = function() {
      if (!this.optionSelectors || this.optionSelectors.length === 0) {
        
        const foundSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
        if (foundSelectors.length > 0) {
          this.optionSelectors = foundSelectors;
        } else {
          const fallbackSelectors = this.querySelectorAll('select, input[type="radio"]');
          this.optionSelectors = fallbackSelectors;
        }
      }
      
      try {
        return original.getSelectedOptions.call(this);
      } catch (error) {
        return [];
      }
    };
    
    // Override updateVariantInput with null safety
    variantPicker.updateVariantInput = function() {
      try {
        if (!this.section) {
          try {
            this.section = this.closest('.js-product');
          } catch (error) {
            // Fallback: search up the DOM tree manually
            let parent = this.parentElement;
            while (parent && !parent.classList.contains('js-product')) {
              parent = parent.parentElement;
            }
            this.section = parent;
          }
          
          if (!this.section) {
            this.section = this;
          }
        }
        
        if (!this.forms && this.section) {
          this.forms = this.section.querySelectorAll('.js-product-form, .js-instalments-form');
        }
        
        if (!this.forms || this.forms.length === 0) {
          this.forms = this.querySelectorAll('form[action*="/cart/add"]') || 
                      document.querySelectorAll('form[action*="/cart/add"]');
        }
        
        if (this.forms && this.forms.length > 0) {
          this.forms.forEach((form) => {
            const input = form.querySelector('[name="id"]');
            if (input) {
              input.value = this.variant ? this.variant.id : '';
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      } catch (error) {
      }
    };
    
    // Override updateAvailability with null safety
    variantPicker.updateAvailability = function() {
      try {
        if (!this.data || !this.data.variants) {
          return;
        }
        
        if (!this.optionSelectors || this.optionSelectors.length === 0) {
          return;
        }
        
        return original.updateAvailability.call(this);
      } catch (error) {
      }
    };
    
    // Override updateAddToCartButton with popup-specific logic
    variantPicker.updateAddToCartButton = function() {
      try {
        if (!this.productForm) {
          return;
        }
        
        if (this.selectedOptions && this.selectedOptions.indexOf(null) > -1) {
          return;
        }
        
        const addBtn = this.productForm.querySelector('[name="add"], button[type="submit"], .btn[type="submit"]');
        if (!addBtn) {
          return;
        }
        
        const variantAvailable = this.variant && this.variant.available;
        const unavailableStr = this.variant ? 
          (window.theme?.strings?.noStock || 'Sold out') : 
          (window.theme?.strings?.noVariant || 'Unavailable - Select options');
        
        // Get the button text element
        const btnTextEl = addBtn.querySelector('.btn-text') || addBtn;
        const originalText = addBtn.dataset.addToCartText || btnTextEl.textContent.trim();
        
        // Store original text if not stored
        if (!addBtn.dataset.addToCartText && originalText && originalText !== unavailableStr) {
          addBtn.dataset.addToCartText = originalText;
        }
        
        // Update button state
        addBtn.disabled = !variantAvailable;
        
        if (variantAvailable) {
          addBtn.classList.remove('btn--unavailable', 'disabled');
          addBtn.style.opacity = '';
          addBtn.style.cursor = '';
          btnTextEl.textContent = addBtn.dataset.addToCartText || originalText;
        } else {
          addBtn.classList.add('btn--unavailable', 'disabled');
          addBtn.style.opacity = '0.6';
          addBtn.style.cursor = 'not-allowed';
          btnTextEl.textContent = unavailableStr;
        }
        
      } catch (error) {
      }
    };
    
    // Override handleVariantChange with comprehensive safety
    variantPicker.handleVariantChange = function(evt) {
      try {
        
        // Ensure we have basic data before proceeding
        if (!this.data) {
          this.data = this.getVariantData();
        }
        
        // Update selections first
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        
        // Check for preSelection
        this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
        
        // Call individual update methods with safety
        const methods = [
          'updateUrl',
          'updateVariantInput', 
          'updateAddToCartButton',
          'updateAvailability',
          'updatePrice',
          'updateBackorderText',
          'updatePickupAvailability',
          'updateSku',
          'updateBarcode'
        ];
        
        methods.forEach(methodName => {
          try {
            if (typeof this[methodName] === 'function') {
              if (methodName === 'updateUrl') {
                this[methodName](evt);
              } else {
                this[methodName]();
              }
            }
          } catch (error) {
          }
        });
        
        // Update label text
        try {
          const VariantPickerClass = customElements.get('variant-picker');
          if (VariantPickerClass && typeof VariantPickerClass.updateLabelText === 'function') {
            VariantPickerClass.updateLabelText(evt);
          }
        } catch (error) {
        }
        
        // Dispatch the variant change event with safe data
        try {
          // Check if we're inside a popup/modal to prevent bubbling to main page
          const isInPopup = this.closest('.cart-upsell-quickbuy-popup, .modal, .popup, [data-popup]');
          
          this.dispatchEvent(new CustomEvent('on:variant:change', {
            bubbles: !isInPopup, // Don't bubble if we're in a popup to prevent affecting main page
            detail: {
              form: this.productForm,
              variant: this.variant,
              allVariants: this.data?.variants || [],
              selectedOptions: this.selectedOptions || [],
              isPopupVariant: !!isInPopup // Flag to identify popup variant changes
            }
          }));
        } catch (error) {
        }
        
        // Also call our custom update methods for the popup
        try {
          const popup = this.closest('.cart-upsell-quickbuy-popup, .modal, .popup, [data-popup]');
          if (popup && window.cartUpsellQuickBuy) {
            setTimeout(() => {
              window.cartUpsellQuickBuy.updateAddToCartButton(popup);
              window.cartUpsellQuickBuy.updateFeaturedImage(popup);
            }, 10);
          }
        } catch (error) {
        }
        
      } catch (error) {
      }
    };
    
  }

  // Add a helper function to make IDs unique within popup
  makePopupIdsUnique(container) {
    const uniqueSuffix = '-popup-' + Date.now();
    
    // Find all elements with IDs and make them unique
    const elementsWithIds = container.querySelectorAll('[id]');
    elementsWithIds.forEach(element => {
      const originalId = element.id;
      const newId = originalId + uniqueSuffix;
      element.id = newId;
      
      // Update any labels that reference this ID
      const labels = container.querySelectorAll(`label[for="${originalId}"]`);
      labels.forEach(label => {
        label.setAttribute('for', newId);
      });
      
      // Update any aria-labelledby or aria-describedby references
      const referencingElements = container.querySelectorAll(`[aria-labelledby*="${originalId}"], [aria-describedby*="${originalId}"]`);
      referencingElements.forEach(refElement => {
        ['aria-labelledby', 'aria-describedby']. forEach(attr => {
          const attrValue = refElement.getAttribute(attr);
          if (attrValue && attrValue.includes(originalId)) {
            const newAttrValue = attrValue.replace(originalId, newId);
            refElement.setAttribute(attr, newAttrValue);
          }
        });
      });
    });
  }
  
  // Update featured image when variant changes
  updateFeaturedImage(container) {
    
    const variantPicker = container.querySelector('variant-picker');
    if (!variantPicker) {
      return;
    }
    
    // Get the current variant
    const currentVariant = variantPicker.variant;
    if (!currentVariant) {
      return;
    }
    
    
    // Try to use theme's built-in MediaGallery functionality first
    const mediaGallery = container.querySelector('media-gallery');
    if (mediaGallery && mediaGallery.setActiveMedia && currentVariant.featured_media) {
      try {
        mediaGallery.setActiveMedia(currentVariant.featured_media.id, true);
        return;
      } catch (error) {
      }
    }
    
    // Fallback: manual image switching
    const imageContainer = container.querySelector('.media-gallery, .main-image, .product-images, .product-media, .quickbuy-gallery');
    
    if (!imageContainer) {
      return;
    }
    
    // Try to find the featured media for this variant
    if (currentVariant.featured_media && currentVariant.featured_media.id) {
      const targetMediaId = currentVariant.featured_media.id;
      
      // Look for existing media elements
      const allMediaElements = imageContainer.querySelectorAll('[data-media-id]');
      
      // Find the target media element
      const targetMediaElement = imageContainer.querySelector(`[data-media-id="${targetMediaId}"]`);
      
      if (targetMediaElement) {
        
        // Remove active class from current elements
        allMediaElements.forEach(el => {
          el.classList.remove('is-active');
          const sliderItem = el.closest('.slider__item');
          if (sliderItem) {
            sliderItem.classList.remove('is-active');
          }
        });
        
        // Add active class to target element
        targetMediaElement.classList.add('is-active');
        const targetSliderItem = targetMediaElement.closest('.slider__item');
        if (targetSliderItem) {
          targetSliderItem.classList.add('is-active');
        }
        
        // Try to trigger slider/carousel change if it exists
        const carousel = imageContainer.querySelector('carousel-slider');
        if (carousel) {
          // Try different methods to update the carousel
          if (carousel.setActiveSlide) {
            const slideIndex = Array.from(carousel.querySelectorAll('.slider__item')).indexOf(targetSliderItem);
            if (slideIndex >= 0) {
              carousel.setActiveSlide(slideIndex);
            }
          } else if (carousel.goToSlide) {
            const slideIndex = Array.from(carousel.querySelectorAll('.slider__item')).indexOf(targetSliderItem);
            if (slideIndex >= 0) {
              carousel.goToSlide(slideIndex);
            }
          }
        }
        
        // Update any thumbnail navigation
        const thumbnails = container.querySelectorAll('.thumbnails [data-media-id]');
        thumbnails.forEach(thumb => {
          thumb.classList.remove('is-active');
          const thumbSliderItem = thumb.closest('.slider__item');
          if (thumbSliderItem) {
            thumbSliderItem.classList.remove('is-active');
          }
        });
        
        const activeThumbnail = container.querySelector(`.thumbnails [data-media-id="${targetMediaId}"]`);
        if (activeThumbnail) {
          activeThumbnail.classList.add('is-active');
          const activeThumbSliderItem = activeThumbnail.closest('.slider__item');
          if (activeThumbSliderItem) {
            activeThumbSliderItem.classList.add('is-active');
          }
        }
        
        
      } else {
        
        // Fallback: try to update the first image src directly
        const firstImage = imageContainer.querySelector('img');
        if (firstImage && currentVariant.featured_media && currentVariant.featured_media.preview_image) {
          const newSrc = currentVariant.featured_media.preview_image.src;
          if (newSrc) {
            // Update the image src to the new variant image
            const newImageUrl = newSrc.includes('?') ? newSrc.split('?')[0] : newSrc;
            const updatedSrc = newImageUrl + '?v=' + Date.now(); // Add cache bust
            firstImage.src = updatedSrc;
            
            // Also update srcset if it exists
            if (firstImage.srcset && currentVariant.featured_media.preview_image.alt) {
              // Try to create a basic srcset for the new image
              const baseUrl = newImageUrl.replace(/(_\d+x\d*)?(\.[^.]*)?$/, '');
              firstImage.srcset = `${baseUrl}_480x.jpg 480w, ${baseUrl}_720x.jpg 720w, ${baseUrl}_960x.jpg 960w`;
            }
            
          }
        }
      }
    } else {
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartUpsellQuickBuy = new CartUpsellQuickBuy();
});

// Export for use in other scripts
window.CartUpsellQuickBuy = CartUpsellQuickBuy;
